import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { AuctionDownloadedService } from '@core/services/auction-downloaded.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LanguageService } from '@shared/services/language.service';
import { MetaManagerService } from '@shared/services/meta-manager.service';
import { environment } from 'src/environments/environment';
import { AuctionView } from '../articles-manager/interfaces/auction.interface';
import { TagInfo } from '../tags-manager/interfaces/tag.interface';
import { TagService } from '../tags-manager/services/tag.service';
import { RegularComponent } from './pages/regular/regular.component';
import { SpecialComponent } from './pages/special/special.component';

@Component({
  selector: 'app-tags-page',
  templateUrl: './tags-page.component.html',
  styleUrls: ['./tags-page.component.scss'],
  standalone: true,
  imports: [SpecialComponent, RegularComponent],
})
@UntilDestroy()
export class TagsPageComponent implements OnInit {
  tagName: string;
  tag: TagInfo | null = null;
  tagNameToShow: string;

  constructor(
    private readonly tagService: TagService,
    private readonly route: ActivatedRoute,
    public languageService: LanguageService,
    private readonly metaManager: MetaManagerService,
    @Inject(DOCUMENT) private readonly _document: Document,
    public auctionDownloadedService: AuctionDownloadedService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe((params: Params) => {
      this.tagName = params['tag'];

      this.tagService
        .getTagWithInfoByDescription(this.tagName)
        .pipe(untilDestroyed(this))
        .subscribe((dataR) => {
          this.tag = dataR;

          if (this.tag !== null && dataR.tagTranslatedList) {
            this.onUpdateMetaObject({
              marketplaceAuctions: [],
              memorabidAuctions: [],
            });
          }
        });
    });
  }

  // TODO: fix types
  // onUpdateMetaObject(values: {
  //   marketplaceAuctions?: AuctionView[];
  //   memorabidAuctions: AuctionView[];
  // })
  onUpdateMetaObject(values: any) {
    const title = (
      (this.tag.tagTranslatedList[0]?.title ||
        this.tag.tagTranslatedList[0]?.translatedValue) ??
      this.tag.description
    ).replace(/"/g, '\\"');
    const description = (
      (this.tag.tagTranslatedList[0]?.description ||
        this.tag.tagTranslatedList[0]?.translatedValue) ??
      this.tag.description
    ).replace(/"/g, '\\"');

    this.metaManager.updateTitleAndMeta(
      title,
      description,
      environment.canonicalUrl +
        this.languageService.userSelectedLanguage.code +
        '/tags/' +
        this.tag.description,
      this.tag.logoFileNameView
    );

    const related = [
      ...values.memorabidAuctions,
      ...(values.marketplaceAuctions || []),
    ].map((article: AuctionView) => ({
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: article.title,
      image: [article.articlePhoto.fullPath],
      keywords: [
        `${environment.canonicalUrl}${this.languageService.userSelectedLanguage.code}/tags/${this.tag.description}`,
      ],
      offers: {
        '@type': 'Offer',
        url: `${environment.canonicalUrl}${this.languageService.userSelectedLanguage.code}/auction/${article.slug}`,
        priceCurrency: 'EUR',
        price: (article.currentMaxOfferedPrice ?? article.basePrice).toFixed(2),
        image: article.articlePhoto.fullPath,
      },
    }));

    const script =
      (this._document.getElementById('json-ld-auction') as HTMLScriptElement) ??
      this._document.createElement('script');
    script.type = `application/ld+json`;
    script.id = `json-ld-auction`;
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ProductGroup',
      name: title,
      image: this.tag!.logoFileNameView,
      description: description,
      keywords: [
        `${environment.canonicalUrl}${this.languageService.userSelectedLanguage.code}/tags/${this.tag.description}`,
      ],
      isRelatedTo: [related.join(', ')],
    });

    this._document.head.appendChild(script);
  }
}
